import type { AgentStep } from "@unbox/api";
import { AlertCircle, ChevronLeft, ChevronRight, Database01, Edit05, RefreshCw01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import type { CategorizedError } from "../lib/error-display";

/**
 * What the answer looked at, as one sideways-scrolling row of labels. The labels are
 * not links: customers see the topic of each query, never its rows or SQL.
 */
export function SourceLabels({ steps }: { steps: AgentStep[] }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () =>
      setEdges({ left: row.scrollLeft > 2, right: row.scrollLeft + row.clientWidth < row.scrollWidth - 2 });
    measure();
    row.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, []);

  if (steps.length === 0) return null;
  const scroll = (direction: 1 | -1) =>
    rowRef.current?.scrollBy({ left: direction * rowRef.current.clientWidth * 0.7, behavior: "smooth" });

  return (
    <div className="ask-sources" style={{ marginBottom: 14 }}>
      <span className="ask-sources-label">Sources</span>
      <div className="ask-sources-track" data-left={edges.left} data-right={edges.right}>
        <div ref={rowRef} className="ask-sources-row">
          {steps.map((step) => (
            <span key={step.step} className="ask-source-link">
              <Database01 width={13} height={13} aria-hidden="true" />
              <span>{step.label}</span>
            </span>
          ))}
        </div>
        {edges.left && (
          <button
            type="button"
            aria-label="Scroll sources left"
            className="ask-sources-arrow ask-sources-arrow--left"
            onClick={() => scroll(-1)}
          >
            <ChevronLeft width={14} height={14} />
          </button>
        )}
        {edges.right && (
          <button
            type="button"
            aria-label="Scroll sources right"
            className="ask-sources-arrow ask-sources-arrow--right"
            onClick={() => scroll(1)}
          >
            <ChevronRight width={14} height={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export function InlineErrorCard({
  err,
  raw,
  question,
  onExplore,
  onRephrase,
}: {
  err: CategorizedError;
  raw: string;
  question?: string;
  onExplore?: (question: string) => void;
  onRephrase?: (question: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div role="alert" className="ask-inline-error">
      <div className="ask-inline-error-head">
        <AlertCircle width={16} height={16} aria-hidden="true" />
        <span>{err.title}</span>
      </div>
      <p className="ask-inline-error-message">{err.message}</p>
      <div className="ask-inline-error-actions">
        {question && (
          <button type="button" className="ask-source-link" onClick={() => onExplore?.(question)}>
            <RefreshCw01 width={13} height={13} aria-hidden="true" />
            <span>Try again</span>
          </button>
        )}
        {question && (
          <button type="button" className="ask-source-link" onClick={() => onRephrase?.(question)}>
            <Edit05 width={13} height={13} aria-hidden="true" />
            <span>Rephrase</span>
          </button>
        )}
        <button
          type="button"
          className="ask-inline-error-details"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide details" : "Details"}
        </button>
      </div>
      {open && <code className="ask-inline-error-raw">{raw}</code>}
    </div>
  );
}
